import type { Action } from 'redux';

import type { GetApiB2bApplicationByIdGetUnitDataResponse } from '/api';
import normaliseError, { type NormaliseErrorInput } from '/shared/functions/normaliseError';
import type { NormalisedError } from '/index';

export type UnitDataCollection = GetApiB2bApplicationByIdGetUnitDataResponse['units'];

export interface UnitDataState {
  fetching: boolean;
  fetchedForApplicationId: number | null;
  saving: boolean;
  saved: boolean;
  fetched: boolean | null;
  error: NormalisedError | null;
  collection: UnitDataCollection;
  loadErrorApplicationId: string | null;
}

interface SetUnitDataStartedAction extends Action {
  type: 'SET_UNIT_DATA_STARTED';
}

interface SetUnitDataRejectedAction extends Action {
  type: 'SET_UNIT_DATA_REJECTED';
  error: NormaliseErrorInput;
}

interface SetUnitDataFulfilledAction extends Action {
  type: 'SET_UNIT_DATA_FULFILLED';
  units: UnitDataCollection;
}

interface GetUnitDataRejectedAction extends Action {
  type: 'GET_UNIT_DATA_REJECTED';
  applicationId: number | string;
  error: NormaliseErrorInput;
}

interface GetUnitDataStartedAction extends Action {
  type: 'GET_UNIT_DATA_STARTED';
  applicationId: number | string;
}

interface GetUnitDataFulfilledAction extends Action {
  type: 'GET_UNIT_DATA_FULFILLED';
  units: UnitDataCollection;
  applicationId?: number;
}

type UnitDataAction =
  | SetUnitDataStartedAction
  | SetUnitDataRejectedAction
  | SetUnitDataFulfilledAction
  | GetUnitDataRejectedAction
  | GetUnitDataStartedAction
  | GetUnitDataFulfilledAction;

const defaultState: UnitDataState = {
  fetching: false,
  fetchedForApplicationId: null,
  saving: false,
  saved: false,
  fetched: null,
  error: null,
  collection: [],
  loadErrorApplicationId: null,
};

function reducer(state: UnitDataState = defaultState, action: UnitDataAction): UnitDataState {
  switch (action.type) {
    case 'SET_UNIT_DATA_STARTED': {
      return {
        ...state,
        saving: true,
        saved: false,
      };
    }
    case 'SET_UNIT_DATA_REJECTED': {
      const { error } = action;
      return {
        ...state,
        error: normaliseError(error),
        saving: false,
      };
    }
    case 'SET_UNIT_DATA_FULFILLED': {
      const { units } = action;
      return {
        ...state,
        collection: units,
        saving: false,
        error: null,
        saved: true,
      };
    }

    case 'GET_UNIT_DATA_REJECTED': {
      const { error } = action;
      return {
        ...defaultState,
        error: normaliseError(error),
        fetching: false,
        fetched: false,
        loadErrorApplicationId: String(action.applicationId),
      };
    }
    case 'GET_UNIT_DATA_STARTED': {
      return {
        ...defaultState,
        fetching: true,
        fetched: false,
      };
    }

    case 'GET_UNIT_DATA_FULFILLED': {
      const { units, applicationId } = action;
      return {
        ...state,
        collection: units,
        error: null,
        fetching: false,
        fetched: true,
        fetchedForApplicationId: applicationId || null,
      };
    }
    default:
      return state;
  }
}

export default reducer;
